import type { HolePenalty } from '@golf/common';
// Jouer un parcours.

import type { ShotLanding, ShotPenalty } from '@golf/common';
import { ApiError, api } from './api.ts';
import { enAttenteSur, enfiler, purgerDependants, signalerReseau } from './file.ts';
import { MAGASIN_ETAT, identifiant, lire, remplacer } from './local.ts';
import type { ClubEnJeu } from './clubChoice.ts';

export type ShotOutcome = 'center' | 'slice' | 'hook' | 'top' | 'fat' | 'shank' | 'miss';

export interface RoundHole {
    number: number;
    par: number | null;
    strokeIndex: number | null;
    /** Distance depuis le départ DU JOUEUR. `null` si le carnet ne la donne pas. */
    distanceM: number | null;
}

export interface RoundPlayer {
    userId: string;
    displayName: string;
    avatarUrl: string | null;
    status: 'pending' | 'accepted' | 'declined';
    holesPlayed: number;
    totalStrokes: number | null;
    teeLabel: string | null;
    handicapIndex: number | null;
    /** La difficulté du DÉPART de ce joueur : deux partenaires peuvent jouer des
     *  repères différents, et ne reçoivent alors pas le même nombre de coups. */
    slope: number | null;
    courseRating: number | null;
    /** Le plafond du handicap de jeu de CE joueur, venu de ses réglages. */
    maxHandicapStrokes: number | null;
}

export interface MyHole {
    holeNumber: number;
    strokes: number | null;
    putts: number | null;
    /** La balle a été relevée : le trou n'a pas été terminé, et `strokes` porte
     *  le maximum retenu plutôt qu'un score joué jusqu'au fond. */
    pickedUp?: boolean;
    /** Les pénalités sans rapport avec un coup, dans l'ordre où elles sont
     *  arrivées. Leur coût se déduit du motif — voir `holePenaltyTotal`. */
    penalties?: HolePenalty[];
}

export interface MyShot {
    holeNumber: number;
    shotIndex: number;
    clubId: string | null;
    suggestedClubId: string | null;
    /** Putts seulement, et coups saisis avant la bascule vers les zones. */
    outcome: ShotOutcome | null;
    /** Où la balle est tombée. `null` sur un putt. */
    landing: ShotLanding | null;
    /** Ce que ce coup a coûté en plus. Le serveur le calcule à partir du
     *  motif : le client ne l'envoie jamais. */
    penaltyStrokes: number;
    penaltyReason: ShotPenalty | null;
    /** Ce coup a mis la balle au fond : le trou est fini. */
    holed: boolean;
    remainingM: number | null;
}

/** Ce que le joueur a fait sur ce trou lors de ses tours precedents. */
export interface HoleHistory {
    holeNumber: number;
    parties: number;
    moyenne: number | null;
    meilleur: number | null;
}

export interface RoundState {
    round: {
        id: string;
        status: 'open' | 'finished';
        startedAt: string;
        endedAt: string | null;
        notes: string | null;
        courseId: string;
        courseName: string;
        city: string | null;
        holeCount: number;
        /** Le plafond du handicap imposé par ce parcours, s'il en impose un. */
        maxHandicapStrokes: number | null;
    } | null;
    holes: RoundHole[];
    players: RoundPlayer[];
    myHoles: MyHole[];
    myShots: MyShot[];
    holeHistory: HoleHistory[];
    /** Le dernier passage sur CE parcours : avec quoi chaque coup a été joué,
     *  et ce qu'il a donné.
     *
     *  L'historique des trous dit COMBIEN de coups ; celui-ci dit avec quel
     *  club. C'est ce qu'on se demande sur le départ — « la dernière fois
     *  j'ai pris le driver, et j'étais hors limites » — et la zone d'arrivée
     *  part donc avec le club : rappeler un choix sans dire s'il était bon
     *  n'aiderait personne.
     *
     *  `null` au premier passage sur le parcours, ou si aucun tour précédent
     *  ne porte de coup détaillé. */
    dernierPassage?: {
        startedAt: string;
        shots: { holeNumber: number; shotIndex: number; clubId: string; landing: string | null }[];
    } | null;
    /** Le dernier parcours joué, tour ouvert ou non. `null` si aucun. */
    lastPlayed?: { courseId: string; courseName: string; playedAt: string } | null;
}

const CLE_SAC = 'sac-du-tour';

/* Le sac emporté. Gardé sur le téléphone pour la même raison que la carte :
 * sans lui, l'écran de jeu ne peut proposer aucun club, et il n'y a plus de
 * coup à saisir du tout. Il ne change pas en cours de tour, ce qui en fait la
 * plus sûre des copies. */
export const getBag = async (): Promise<{ clubs: ClubEnJeu[] }> => {
    try {
        /* Le fuseau part avec la requête : le serveur découpe les séances de
         * practice en JOURS pour bâtir la fenêtre de chaque club, et seul le
         * navigateur sait dans quel fuseau le joueur a tapé ses balles. */
        const sac = await api<{ clubs: ClubEnJeu[] }>(
            `/rounds/bag?tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`,
        );
        signalerReseau(true);
        await remplacer(MAGASIN_ETAT, sac, CLE_SAC);
        return sac;
    } catch (e) {
        if (e instanceof ApiError) throw e;
        signalerReseau(false);
        const copie = await lire<{ clubs: ClubEnJeu[] }>(MAGASIN_ETAT, CLE_SAC);
        if (copie) return copie;
        throw e;
    }
};

/* Les écritures d'un tour passent toutes par la FILE.
 *
 * Un golf est un endroit sans réseau, et l'écran de jeu ne doit jamais
 * attendre : chaque appel ci-dessous range son écriture sur le téléphone et
 * rend la main tout de suite. La file la portera au serveur dès qu'il répond.
 *
 * Aucun de ces appels ne se servait de la réponse du serveur — les écrans
 * tiennent déjà leur propre état — ce qui rend la bascule sans effet de bord.
 * Le seul qui rendait quelque chose d'utile est le départ, et ce quelque chose
 * est un identifiant : il est désormais tiré ici. */

/* Ouvre un tour et rend son identifiant, sans attendre le serveur.
 *
 * ── Pourquoi l'écran de jeu a besoin d'un aperçu ────────────────────────────
 *
 * L'écriture part dans la file, donc le tour n'existe pas encore au serveur
 * quand l'écran suivant s'ouvre — et cet écran demandait « quel est mon tour en
 * cours ? » à un serveur qui répondait honnêtement « aucun ». Il fallait
 * revenir en arrière pour que la file ait eu le temps de passer, et reprendre
 * un tour qui avait pourtant bien commencé.
 *
 * On compose donc ICI l'état que le serveur aurait rendu, avec ce que l'écran
 * de départ a déjà sous la main : le carnet du parcours, le départ choisi, soi,
 * et les partenaires invités. Il est rangé comme instantané, et l'écran de jeu
 * l'y trouve — avec ou sans réseau, ce qui règle du même coup le départ pris au
 * premier tee dans un creux sans couverture.
 *
 * Ce n'est pas une copie approximative : ce sont les mêmes trous, le même
 * départ et les mêmes joueurs. Ce qui manque — l'historique de ses propres
 * scores sur chaque trou — n'existe que pour enrichir, et revient à la première
 * relecture une fois la file passée. */
export const startRound = async (body: {
    courseId: string; teeId?: string | null; friendIds?: string[]; bagId?: string | null;
}, apercu: {
    courseName: string;
    city?: string | null;
    holes: RoundHole[];
    /** Le libellé du départ retenu, tel qu'il s'écrit sur la carte. */
    teeLabel?: string | null;
    moi: { userId: string; displayName: string; avatarUrl?: string | null };
    invites?: { userId: string; displayName: string; avatarUrl?: string | null }[];
}): Promise<string> => {
    const id = identifiant();
    const joueur = (
        j: { userId: string; displayName: string; avatarUrl?: string | null },
        status: RoundPlayer['status'],
        teeLabel: string | null,
    ): RoundPlayer => ({
        userId: j.userId,
        displayName: j.displayName,
        avatarUrl: j.avatarUrl ?? null,
        status,
        holesPlayed: 0,
        totalStrokes: null,
        teeLabel,
        handicapIndex: null,
        // La difficulté du départ et l'index reviennent du serveur à la première
        // relecture : l'aperçu ouvre la carte, il ne calcule pas de net.
        slope: null,
        courseRating: null,
        maxHandicapStrokes: null,
    });

    await garderTour({
        round: {
            id,
            status: 'open',
            startedAt: new Date().toISOString(),
            endedAt: null,
            notes: null,
            courseId: body.courseId,
            courseName: apercu.courseName,
            city: apercu.city ?? null,
            holeCount: apercu.holes.length,
            // Le plafond revient du serveur à la première relecture : l'aperçu
            // ouvre la carte, il ne calcule pas de net.
            maxHandicapStrokes: null,
        },
        holes: apercu.holes,
        players: [
            joueur(apercu.moi, 'accepted', apercu.teeLabel ?? null),
            // Les invités n'ont pas encore répondu : c'est aussi ce que le
            // serveur dirait, et l'écran ne doit pas les compter comme présents.
            ...(apercu.invites ?? []).map((j) => joueur(j, 'pending', null)),
        ],
        myHoles: [],
        myShots: [],
        holeHistory: [],
    });

    await enfiler({
        id, cree: id, method: 'POST', path: '/rounds', body: { ...body, id },
        quoi: 'Départ d’un tour',
    });
    return id;
};

/* Le tour en cours, du serveur ou du téléphone.
 *
 * ── Pourquoi un instantané local ────────────────────────────────────────────
 *
 * On ressort son téléphone au septième trou, dans un creux sans réseau, et
 * l'écran de jeu doit s'ouvrir sur la carte en cours. Sans copie locale il ne
 * pouvait rien afficher : le tour n'existait que sur le serveur.
 *
 * ── Qui l'emporte, du serveur ou de la copie ────────────────────────────────
 *
 * La copie, tant que la file n'est pas vide. Une réponse du serveur qui ne
 * porte pas encore les coups en attente est EN RETARD sur l'écran : l'appliquer
 * ferait disparaître un score qu'on vient de saisir, puis réapparaître à la
 * synchronisation. Un score qui clignote est un score qu'on cesse de croire.
 */
export const getCurrentRound = async (): Promise<RoundState> => {
    if (await enAttenteSur('/rounds')) {
        const copie = await lire<RoundState>(MAGASIN_ETAT, CLE_TOUR);
        if (copie) return copie;
    }
    try {
        const etat = await api<RoundState>('/rounds/current');
        signalerReseau(true);
        await garderTour(etat);
        return etat;
    } catch (e) {
        // Un refus du serveur est une vraie réponse : on la laisse remonter.
        // Seule l'absence de réseau justifie de servir la copie.
        if (e instanceof ApiError) throw e;
        signalerReseau(false);
        const copie = await lire<RoundState>(MAGASIN_ETAT, CLE_TOUR);
        if (copie) return copie;
        throw e;
    }
};

const CLE_TOUR = 'tour-en-cours';

/** Garde l'état du tour sous la main. Appelé par l'écran de jeu à chaque
 *  changement : c'est lui qui tient la vérité entre deux synchronisations. */
export const garderTour = (etat: RoundState | null) =>
    remplacer(MAGASIN_ETAT, etat, CLE_TOUR).then(() => undefined);

export const getRound = (id: string) => api<RoundState>(`/rounds/${id}`);

/** La carte d'un trou. Rejouable : le serveur en fait un remplacement. */
export const saveHole = (id: string, n: number, body: {
    strokes: number | null; putts?: number | null; pickedUp?: boolean;
    penalties?: HolePenalty[];
}) => enfiler({
    method: 'PUT', path: `/rounds/${id}/holes/${n}`, body,
    quoi: `Score du trou ${n}`,
}).then(() => undefined);

export const saveShot = (id: string, body: {
    holeNumber: number;
    shotIndex: number;
    clubId?: string | null;
    suggestedClubId?: string | null;
    outcome?: ShotOutcome | null;
    landing?: ShotLanding | null;
    penaltyReason?: ShotPenalty | null;
    holed?: boolean;
    remainingM?: number | null;
}) => enfiler({
    method: 'POST', path: `/rounds/${id}/shots`, body,
    quoi: `Coup ${body.shotIndex} du trou ${body.holeNumber}`,
}).then(() => undefined);

/** Retirer un coup et tout ce qu'il portait — son club, sa zone, sa pénalité. */
export const deleteShot = (id: string, holeNumber: number, shotIndex: number) =>
    enfiler({
        method: 'DELETE', path: `/rounds/${id}/shots/${holeNumber}/${shotIndex}`,
        quoi: `Coup ${shotIndex} défait au trou ${holeNumber}`,
    }).then(() => undefined);

/* Terminer un tour passe aussi par la file : on rentre au parking, souvent
 * sans plus de réseau qu'au quinzième, et la carte doit se fermer quand même. */
export const finishRound = (id: string) => enfiler({
    method: 'POST', path: `/rounds/${id}/finish`,
    quoi: 'Fin du tour',
}).then(() => undefined);

export interface RoundSummary {
    id: string;
    startedAt: string;
    endedAt: string | null;
    status: 'open' | 'finished';
    courseId: string;
    courseName: string;
    city: string | null;
    holesPlayed: number;
    totalStrokes: number | null;
    teeLabel: string | null;
    /** Vrai si j'ai organise ce tour : seul l'hote peut l'effacer en entier. */
    isHost: boolean;
    playerCount: number;
}

export const listRounds = () =>
    api<{ rounds: RoundSummary[] }>('/rounds').then((r) => r.rounds);

export interface RoundStatsPoint {
    id: string;
    startedAt: string;
    courseName: string;
    totalStrokes: number;
    ecart: number;
}

/** Le putting sur le parcours, sur la même fenêtre que le reste de l'écran.
 *
 *  « En régulation », c'est DEUX putts : ce que le par d'un trou réserve au
 *  green. Une moyenne seule noierait les trois-putts dans les un-putt. */
export interface RoundPutting {
    holes: number;
    regulation: number;
    onePutt: number;
    twoPutt: number;
    threePutt: number;
    avgPutts: number | null;
}

/** Où se perdent les coups : le long jeu, ou le petit jeu.
 *
 *  Aucune de ces mesures ne dit rien seule — c'est leur croisement qui
 *  répond. Manquer ses greens en sauvant beaucoup, c'est tenir son score
 *  grâce au petit jeu, donc devoir travailler le swing. */
export interface RoundJeu {
    /** Trous où un départ a été saisi, par 3 exclus. */
    teeHoles: number;
    fairways: number;
    /** Trous joués dont on peut dire si le green a été pris en régulation. */
    greenHoles: number;
    greens: number;
    /** Trous où le green a été manqué en régulation et qui ont été finis. */
    scrambleHoles: number;
    scrambles: number;
}

/** Un club, tel que le PARCOURS l'a vu.
 *
 *  Le practice dit ce qu'un club rend quand on le travaille ; ceci dit ce qu'on
 *  en fait en jouant. Les quatre positions de départ ne totalisent pas
 *  forcément les coups du club : un putt part du green, et un coup joué après
 *  un drop part d'un endroit que personne n'a saisi. */
export interface RoundClub {
    clubId: string;
    label: string;
    brand: string | null;
    nickname: string | null;
    category: string;
    loftDeg: string | null;
    shots: number;
    tee: number;
    fairway: number;
    rough: number;
    bunker: number;
}

export interface RoundStats {
    /** Le tour regardé, quand l'écran n'en regarde qu'un. `null` sinon. */
    tour: { id: string; courseName: string; startedAt: string } | null;
    /** Les coups reçus sur ce tour : ce qui sépare le brut du net.
     *
     *  Le nombre plutôt que le net lui-même, parce que c'est lui qui explique
     *  l'écart. `null` hors d'un tour précis — les coups reçus dépendent du
     *  départ joué, une moyenne de dix tours n'en a pas — et `total` est `null`
     *  sans index connu. */
    coupsRecus: { total: number | null; approche: boolean; plafonne: boolean } | null;
    trend: RoundStatsPoint[];
    rounds: number;
    avgEcart: number | null;
    avgPuttsRound: number | null;
    avgPuttsHole: number | null;
    best: {
        courseName: string; city: string | null; startedAt: string;
        totalStrokes: number; ecart: number;
    } | null;
    /** `null` si aucun putt n'a été saisi sur la fenêtre. */
    putting: RoundPutting | null;
    /** `null` tant qu'aucune zone d'arrivée n'a été saisie. */
    jeu: RoundJeu | null;
    distribution: { eagle: number; birdie: number; par: number; bogey: number; double: number };
    /** Les clubs joués, dans l'ordre du sac. Vide tant qu'aucun club n'a été
     *  attaché à un coup. */
    clubs: RoundClub[];
    /** Le sable : combien de fois la balle y est entrée, et combien de coups
     *  joués depuis le sable y sont restés. */
    sable: { entres: number; echecs: number };
    /** Les coups de pénalité de la fenêtre, tous motifs confondus — ceux d'un
     *  coup parti à l'eau comme ceux attachés au trou.
     *
     *  À distinguer de `grosScores.penalties`, qui ne compte que celles des
     *  trous rendus à +2 ou pire : une pénalité prise sur un bogey n'y figure
     *  pas et coûte pourtant un coup. `0` est ici une vraie réponse et non
     *  l'absence de donnée : une pénalité se saisit explicitement. */
    penalites: number;
    /** Où sont partis les coups sur les trous rendus à +2 ou pire.
     *
     *  Les trois parts — pénalités, jeu long, putting — s'additionnent
     *  EXACTEMENT aux coups perdus : un par, c'est (par − 2) coups pour le
     *  green et deux putts, et tout ce qui dépasse se range dans l'une des
     *  trois sans recouvrement. */
    grosScores: {
        holes: number;
        playedHoles: number;
        lost: number;
        penalties: number;
        putting: number;
        /** Les coups que le putting a RENDUS sur ces trous — un putt unique là
         *  où le par en réserve deux. Comptés à part : ils n'allègent pas le jeu
         *  long, à qui ils n'appartiennent pas. */
        puttingSaved: number;
        longGame: number;
        /** Combien de ces trous sont passés par un bunker. Du contexte, pas un
         *  compte de coups : un bunker ne coûte pas un nombre fixe. */
        throughBunker: number;
    };
}

/** Les statistiques de parcours. Bornées à UN tour quand on en passe un :
 *  mêmes mesures, mêmes définitions, une autre échelle.
 *
 *  `tour` prend un identifiant, ou le mot `dernier` — le dernier tour rendu,
 *  que le serveur désigne lui-même. L'accueil en a besoin sans connaître
 *  l'identifiant, et le faire chercher d'abord coûterait deux allers-retours
 *  enchaînés. `tour` revient alors rempli dans la réponse, avec le nom du
 *  parcours et la date. */
export const getRoundStats = (tour?: string) =>
    api<RoundStats>(`/rounds/stats${tour ? `?tour=${encodeURIComponent(tour)}` : ''}`);

/** Efface un tour. L'hote emporte la partie entiere ; un invite ne retire que
 *  sa propre participation. */
/* Quitter un tour, ou l'effacer — c'est le même geste, et il efface la
 * participation.
 *
 * Ce qui attendait dans la file n'a donc plus d'objet : les trous d'un tour
 * qu'on vient de quitter n'ont plus de place où atterrir. On les jette AVANT
 * de partir, plutôt que de les laisser se faire refuser un par un.
 *
 * Et si le départ lui-même n'était pas encore parti, le tour n'a jamais existé
 * ailleurs que sur ce téléphone : il n'y a rien à effacer au serveur. */
export const deleteRound = async (id: string): Promise<void> => {
    const jetees = await purgerDependants(id);
    if (jetees.some((e) => e.cree === id)) return;
    await enfiler({
        method: 'DELETE', path: `/rounds/${id}`,
        quoi: 'Tour quitté',
    });
};

/** Le score par rapport au par, tel qu'on le dit sur un parcours.
 *
 *  Le trou en un passe avant tout le reste : une balle rentree du depart ne
 *  s'appelle pas un eagle, quel que soit le par. C'est le seul score qui porte
 *  un nom independant de la longueur du trou, et le seul dont on se souvient
 *  vingt ans apres. */
export function ecartAuPar(strokes: number, par: number | null): string {
    if (strokes === 1) return 'Trou en un';
    if (par == null) return String(strokes);
    const d = strokes - par;
    if (d === 0) return 'Par';
    if (d === -1) return 'Birdie';
    if (d === -2) return 'Eagle';
    if (d <= -3) return 'Albatros';
    if (d === 1) return 'Bogey';
    if (d === 2) return 'Double';
    return `+${d}`;
}

// -- Le multijoueur ---------------------------------------------------------

export interface RoundInvitation {
    roundId: string;
    courseName: string;
    city: string | null;
    hostName: string | null;
    hostAvatarUrl: string | null;
    invitedAt: string;
}

export const listRoundInvitations = () =>
    api<{ invitations: RoundInvitation[] }>('/rounds/invitations').then((r) => r.invitations);

/** Repondre a une invitation. Le depart est choisi a ce moment-la : sur un
 *  meme tour, chacun part de sa marque, et c'est elle qui fixe les distances
 *  du carnet donc les clubs proposes. */
export const answerRound = (id: string, accept: boolean, teeId?: string | null) =>
    api<RoundState | void>(`/rounds/${id}/answer`, { method: 'POST', body: { accept, teeId } });

// -- La carte de fin de tour ------------------------------------------------

export interface CartePlayer {
    userId: string;
    displayName: string;
    avatarUrl: string | null;
    holesPlayed: number;
    totalStrokes: number | null;
    teeLabel: string | null;
    handicapIndex: number | null;
    slope: number | null;
    courseRating: number | null;
    maxHandicapStrokes: number | null;
    holes: MyHole[];
}

export interface CarteTour {
    round: {
        id: string;
        status: 'open' | 'finished';
        /** MA carte est rendue. Distinct du statut du tour : un partenaire qui
         *  joue encore laisse la partie ouverte sans que la mienne le soit. */
        myFinished: boolean;
        startedAt: string;
        endedAt: string | null;
        courseId: string;
        courseName: string;
        city: string | null;
        holeCount: number;
        maxHandicapStrokes: number | null;
    };
    holes: RoundHole[];
    players: CartePlayer[];
}

/** La carte de TOUT LE MONDE. L'etat de jeu ne rend que la sienne : en
 *  marchant on ne compare pas, au club-house on ne fait que ca. */
export const getCarteTour = (id: string) => api<CarteTour>(`/rounds/${id}/carte`);
